import { useCustomerService as useCoreCustomerService, ZendeskFieldIds, ZendeskBu } from '@myfunnow/web-core'

import { useUserInfo } from '@/composition/useMember'
import { useBreakpoints } from '@/composition/useBreakpoints'
import { useLowerCase } from '@/composition/useIntl'

import { usePlatformStore } from '@/store-pinia/platform'
import { useRegionStore } from '@/store-pinia/region'

import { checkIfFunkiPage, checkIfContactPage, checkIfOrderListPage, checkIfOrderDetailPage, checkIfAttendeeFormPage } from '@/utils/pageHelpers'
import * as loginService from '@/service/login'

import { MembershipLevel } from '@/view-models/member'

export const shouldCsBubbleShow = ref(false)

export function useCustomerService() {
  const nuxtApp = useNuxtApp()
  const { $axios, $store: store } = nuxtApp
  const config = useRuntimeConfig()
  const zendeskKey = config.public.ZENDESK_KEYS.FUNNOW

  const platformStore = usePlatformStore()
  const regionStore = useRegionStore()
  const { isFunBookV2 } = storeToRefs(platformStore)
  const { regionDetectInfo } = storeToRefs(regionStore)

  const { userInfo } = useUserInfo()
  const { breakpoints } = useBreakpoints()
  const { smAndUp: isDesktopScreen } = toRefs(breakpoints)

  const { localeProperties } = useI18n()
  const { getLocaleLowerCaseString } = useLowerCase()

  const route = useRoute()

  const isLogin = computed(() => !!userInfo.value)
  const membershipTier = computed(() => {
    const level = userInfo.value?.benefit?.level
    if (level !== undefined) {
      return getLocaleLowerCaseString(MembershipLevel[level])
    }
    return 'non-member'
  })
  const lowerCasedLocale = computed(() => getLocaleLowerCaseString(localeProperties.value.language))

  const indexRegion = computed(() => store.getters['region/indexRegion'])

  const isCsBubbleShown = computed(() => {
    const isFunkiPage = checkIfFunkiPage(route.name)
    const isContactUsPage = checkIfContactPage(route.name)
    const isOrderListPage = checkIfOrderListPage(route.name)
    const isOrderDetailPage = checkIfOrderDetailPage(route.name)
    const isAttendeeFormPage = checkIfAttendeeFormPage(route.name)

    if (!isLoaded.value || isFunBookV2.value || isFunkiPage) {
      return false
    }

    if (isOrderDetailPage || isAttendeeFormPage) {
      return shouldCsBubbleShow.value
    }
    if (isOrderListPage || isContactUsPage) {
      return true
    }
    if (isDesktopScreen.value && hasOpened.value) {
      return true
    }

    return false
  })

  const baseFields = computed(() => {
    const conversationFields = [
      { id: ZendeskFieldIds.USER_EMAIL, value: userInfo.value?.email || '' },
      { id: ZendeskFieldIds.USER_ID, value: `${userInfo.value?.uid || ''}` },
      { id: ZendeskFieldIds.USER_PHONE, value: userInfo.value?.phone || '' },
      { id: ZendeskFieldIds.MEMBERSHIP_TIER, value: membershipTier.value },
      { id: ZendeskFieldIds.BU, value: ZendeskBu.FUNNOW },
      { id: ZendeskFieldIds.COUNTRY, value: indexRegion.value?.country_code ?? (regionDetectInfo.value?.country_code || '') },
      { id: ZendeskFieldIds.LANGUAGE, value: lowerCasedLocale.value },
      { id: ZendeskFieldIds.CONTACT_TYPE, value: 'b2c' },
      { id: ZendeskFieldIds.CHANNEL, value: 'web' },
    ]

    return conversationFields
  })

  const { initial, handleOpen, hasOpened, hasUnreadMessages, isLoaded, setProperties } = useCoreCustomerService({
    isLogin,
    zendeskKey,
    baseFields,
    getZendeskJwt: () => loginService.getZendeskJwt($axios),
  })

  return {
    initialCs: initial,
    handleOpen,
    setProperties,
    isCsBubbleShown,
    isCsLoaded: isLoaded,
    hasUnreadMessages,
  }
}
